import { Input } from '@/components/form/Input';
import { Container } from '@/components/legacy-ui';
import { Spinner } from '@/components/ui/spinner';
import { CostWindow } from '@/hooks/useAccountCost';
import { NegotiatedPriceInput } from '@/sections/settings/subscription/components';
import { DateRangePicker } from '@/sections/settings/subscription/components/DateRangePicker';
import {
  frequencyScaleFactor,
  marginColor,
} from '@/sections/settings/subscription/configure/components/costMargin.utils';
import {
  frequencyLabel,
  SUBSCRIPTION_INPUT_CLASS,
  subscriptionTierDisplayName,
} from '@/sections/settings/subscription/constant';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { parseNonNegativeInt } from '@/utils/number.util';

export interface CostDateRange {
  selectedDays: number | null;
  customFrom: string;
  customTo: string;
}

interface ReviewSectionProps {
  localSubscription: SubscriptionConfiguration;
  tierIncludesWebApps: boolean;
  isPraetorianUser: boolean;
  totalMsrp: number;
  subscriptionFloor: number;
  intelligenceTierName: string;
  intelligenceAnnualCost: number;
  subscriptionMsrp: number;
  usagePrice: number;
  onEstimatedCostChange: (value: number) => void;
  costWindow?: CostWindow;
  costLoading?: boolean;
  historicalScanFrequency?: string;
  costDateRange: CostDateRange;
  onCostPreset: (days: number) => void;
  onCostCustomFrom: (v: string) => void;
  onCostCustomTo: (v: string) => void;
}

export function ReviewSection({
  localSubscription,
  tierIncludesWebApps,
  isPraetorianUser,
  totalMsrp,
  subscriptionFloor,
  intelligenceTierName,
  intelligenceAnnualCost,
  subscriptionMsrp,
  usagePrice,
  onEstimatedCostChange,
  costWindow,
  costLoading,
  historicalScanFrequency,
  costDateRange,
  onCostPreset,
  onCostCustomFrom,
  onCostCustomTo,
}: ReviewSectionProps) {
  const tierDisplayName = subscriptionTierDisplayName(
    localSubscription.selectedTier
  );

  return (
    <>
      <h3 className="text-chariot-text-primary text-lg font-semibold">
        Review Subscription
      </h3>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Left column: Configuration summary */}
        <Container className="bg-chariot-background-primary space-y-2">
          {/* Subscription details */}
          <SummaryRow label="Tier" value={tierDisplayName} />
          <SummaryRow
            label="Scan Frequency"
            value={frequencyLabel(localSubscription.scanSchedule)}
          />
          <SummaryRow
            label="Asset Coverage"
            value={`${localSubscription.numberOfAssets.toLocaleString()} assets`}
          />
          {tierIncludesWebApps &&
            localSubscription.numberOfApplications > 0 && (
              <SummaryRow
                label="Web FQDNs (DAST)"
                value={`${localSubscription.numberOfApplications.toLocaleString()} apps`}
              />
            )}
          {(localSubscription.selectedTier === 'ultimate' ||
            localSubscription.selectedTier === 'ludacris') &&
            (localSubscription.numberOfAEVExercises ?? 0) > 0 && (
              <SummaryRow
                label="AEV Exercises"
                value={`${localSubscription.numberOfAEVExercises}/year`}
              />
            )}
          {localSubscription.selectedTier === 'ludacris' &&
            (localSubscription.numberOfRedTeams ?? 0) > 0 && (
              <SummaryRow
                label="Red Teams"
                value={`${localSubscription.numberOfRedTeams}/year`}
              />
            )}
          {localSubscription.startDate && (
            <SummaryRow
              label="Term"
              value={`${localSubscription.startDate} — ${localSubscription.endDate || 'TBD'}`}
            />
          )}

          {/* Guard Intelligence - merged into same card with a subtle separator */}
          <div className="border-chariot-stroke/50 border-t pt-2">
            <SummaryRow
              label="Guard Intelligence"
              value={intelligenceTierName}
            />
            <SummaryRow
              label="Intelligence Cost"
              value={`$${intelligenceAnnualCost.toLocaleString()}/yr`}
            />
          </div>
        </Container>

        {/* Right column: Pricing */}
        {isPraetorianUser ? (
          <div className="space-y-3">
            <Container
              isSelected
              className="bg-chariot-background-primary space-y-2"
            >
              <SummaryRow
                label="Subscription MSRP"
                value={`$${subscriptionMsrp.toLocaleString()}`}
                bold
              />
              {intelligenceAnnualCost > 0 && (
                <SummaryRow
                  label="Intelligence Cost"
                  value={`$${intelligenceAnnualCost.toLocaleString()}`}
                  bold
                />
              )}
              <div className="border-chariot-stroke flex items-center justify-between border-t pt-2">
                <span className="text-chariot-text-primary text-sm font-bold">
                  Total MSRP
                </span>
                <span className="text-chariot-brand-primary text-xl font-bold">
                  ${totalMsrp.toLocaleString()}
                </span>
              </div>
              <div className="text-chariot-text-secondary text-right text-xs">
                Floor: ${subscriptionFloor.toLocaleString()}
              </div>
            </Container>

            <NegotiatedPriceInput
              value={localSubscription.estimatedCost}
              onChange={onEstimatedCostChange}
              msrp={totalMsrp}
              usagePrice={usagePrice}
              floorPrice={subscriptionFloor}
            />

            <CostMarginPanel
              costWindow={costWindow}
              costLoading={costLoading}
              proposedPrice={localSubscription.estimatedCost}
              proposedFrequency={localSubscription.scanSchedule}
              historicalFrequency={historicalScanFrequency}
              costDateRange={costDateRange}
              onCostPreset={onCostPreset}
              onCostCustomFrom={onCostCustomFrom}
              onCostCustomTo={onCostCustomTo}
            />
          </div>
        ) : (
          <Container
            isSelected
            className="bg-chariot-background-primary flex items-center justify-between"
          >
            <h2 className="text-chariot-text-primary text-sm font-medium">
              Estimated Annual Price
            </h2>
            <div className="text-chariot-brand-primary text-2xl font-bold">
              <Input
                name="estimatedCost"
                className={cn(SUBSCRIPTION_INPUT_CLASS, 'text-right')}
                value={localSubscription.estimatedCost.toString()}
                onChange={e =>
                  onEstimatedCostChange(parseNonNegativeInt(e.target.value))
                }
                placeholder="Enter price"
              />
            </div>
          </Container>
        )}
      </div>
    </>
  );
}

function CostMarginPanel({
  costWindow,
  costLoading,
  proposedPrice,
  proposedFrequency,
  historicalFrequency,
  costDateRange,
  onCostPreset,
  onCostCustomFrom,
  onCostCustomTo,
}: {
  costWindow?: CostWindow;
  costLoading?: boolean;
  proposedPrice: number;
  proposedFrequency: string;
  historicalFrequency?: string;
  costDateRange: CostDateRange;
  onCostPreset: (days: number) => void;
  onCostCustomFrom: (v: string) => void;
  onCostCustomTo: (v: string) => void;
}) {
  const scale = frequencyScaleFactor(
    historicalFrequency ?? proposedFrequency,
    proposedFrequency
  );
  const freqChanged = scale !== 1;

  const annualCompute = (costWindow?.annual_cogs_compute ?? 0) * scale;
  const annualAI = costWindow?.annual_cogs_ai ?? 0;
  const annualCOGS = annualCompute + annualAI;

  const margin =
    proposedPrice > 0
      ? ((proposedPrice - annualCOGS) / proposedPrice) * 100
      : annualCOGS > 0
        ? -100
        : null;

  return (
    <Container className="bg-chariot-background-primary space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-chariot-text-primary text-sm font-semibold">
          P&L (COGS)
        </span>
        <DateRangePicker
          selectedDays={costDateRange.selectedDays}
          customFrom={costDateRange.customFrom}
          customTo={costDateRange.customTo}
          onPreset={onCostPreset}
          onCustomFrom={onCostCustomFrom}
          onCustomTo={onCostCustomTo}
        />
      </div>

      {costLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : !costWindow ? (
        <p className="text-chariot-text-secondary py-2 text-center text-xs">
          No cost data available
        </p>
      ) : (
        <>
          <SummaryRow
            label="Compute"
            value={`$${Math.round(annualCompute).toLocaleString()}`}
          />
          {freqChanged && (
            <p className="text-chariot-text-secondary -mt-1 ml-2 text-xs italic">
              Scaled {scale.toFixed(1)}x (
              {frequencyLabel(historicalFrequency ?? '')} →{' '}
              {frequencyLabel(proposedFrequency)})
            </p>
          )}
          <SummaryRow
            label="AI"
            value={`$${Math.round(annualAI).toLocaleString()}`}
          />
          <SummaryRow
            label="Total COGS"
            value={`$${Math.round(annualCOGS).toLocaleString()}`}
            bold
          />
          <div className="border-chariot-stroke border-t pt-2">
            <div className="flex items-center justify-between">
              <span className="text-chariot-text-secondary text-sm">
                Gross Margin
              </span>
              <span className={cn('text-sm font-bold', marginColor(margin))}>
                {margin !== null ? `${margin.toFixed(1)}%` : '—'}
              </span>
            </div>
            {proposedPrice > 0 && (
              <div className="text-chariot-text-secondary mt-1 text-right text-xs">
                ${Math.round(proposedPrice - annualCOGS).toLocaleString()} net
                on ${proposedPrice.toLocaleString()} ACV
              </div>
            )}
            {proposedPrice === 0 && annualCOGS > 0 && (
              <div className="text-chariot-text-secondary mt-1 text-right text-xs">
                ${Math.round(annualCOGS).toLocaleString()}/yr cost — no ACV set
              </div>
            )}
          </div>
        </>
      )}
    </Container>
  );
}

function SummaryRow({
  label,
  value,
  capitalize,
  bold,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-chariot-text-secondary text-sm">{label}</span>
      <span
        className={cn(
          'text-chariot-text-primary text-sm',
          capitalize && 'capitalize',
          bold ? 'font-bold' : 'font-medium'
        )}
      >
        {value}
      </span>
    </div>
  );
}
